import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createPersonDefaults, type CreatePersonDefaultsOptions } from './person-defaults.factory.js';
import type { PersonIcon } from './person-icons.config.js';

/**
 * Subscribes to `place/person` UiActions and materialises a new person:
 * builds the PersonObject via `createPersonDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` and
 * `edit/enter-plain {field:'name'}` for the new id (TR6, TR7).
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC4: never publishes onto `selection$`/`tool$`/`viewport$` directly; downstream
 * adapters translate `selection/set` into a `selection$` write.
 *
 * FC17: dispatches the dedicated `edit/enter-plain` discriminator (with
 * `field:'name'`) — never the sticky `edit/enter-sticky` action — so the
 * `PlainEditController` and `StickyEditorController` cannot cross-talk.
 *
 * Supports both bare-click placement (no `size`, person centred on pointer)
 * and drag-to-create (with `size`, pointer = top-left of dragged box).
 */
export class PersonPlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/person' }> =>
                        a.type === 'place/person',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[PersonPlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/person' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        const sizeOpt = action.size ?? null;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts.
        // Bugreport (Anke, 2026-09-03): "bei der person... ist fast nichts
        // korrekt angeschlossen" — only `icon` was ever read here; every
        // other stageable person property is wired through now too.
        const staged = this.#bus.stagedToolProperties$.value.person;
        const overrides: CreatePersonDefaultsOptions | undefined = staged && {
            icon: staged['icon'] as PersonIcon | undefined,
            circleFill: staged['circleFill'] as string | undefined,
            circleStroke: staged['circleStroke'] as string | undefined,
            circleStrokeWidth: staged['circleStrokeWidth'] as number | undefined,
            circleStrokeStyle: staged['circleStrokeStyle'] as 'solid' | 'dashed' | 'dotted' | undefined,
            backgroundOpacity: staged['backgroundOpacity'] as number | undefined,
            boxShadow: staged['boxShadow'] as 'on' | undefined,
            textColor: staged['textColor'] as string | undefined,
            fontSize: staged['fontSize'] as number | undefined,
            fontWeight: staged['fontWeight'] as number | undefined,
            fontStyle: staged['fontStyle'] as 'italic' | undefined,
            textDecoration: staged['textDecoration'] as 'underline' | 'line-through' | undefined,
        };
        const person = createPersonDefaults(
            scale,
            { x: action.worldX, y: action.worldY },
            sizeOpt,
            overrides,
        );

        const cmd = new AddObjectCommand(person);
        this.#history.push(cmd, this.#state);

        const newId = objectId(person.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
        this.#bus.uiActions$.next({ type: 'edit/enter-plain', id: newId, field: 'name' });
    }
}
